import React, { Component } from 'react';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { FaPlus } from 'react-icons/fa';
import intlShape from '@/shared/utils/intlShape';
import { listItemMsg } from '@/shared/components/FormattedListValue';

import PropTypes from 'prop-types';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import { CanCreate, showWhenPermission } from '@/shared/components/security';
import handleHttpError from '@/shared/services/HandleHttpError';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import api from '../../../shared/api';
import config from '../../../config';
import ToastrService from '../../../shared/services/ToastrService';

import WorkflowConfigurationTable from './WorkflowConfigurationTable';
import WorkflowConfigurationModal from './WorkflowConfigurationModal';

import messages from './workflowConfigurationMessages';

class WorkflowConfigurationContainer extends Component {
  state = {
    configurations: [],
    configurationModal: null,
    saving: false,
    showWorkflowConfigurationModal: false,
  };

  componentDidMount() {
    const { intl } = this.props;
    const workflowConfigurationEndpoint = `${config.appUrls.api}/api/workflowConfiguration`;
    const taskTemplatesPromise = api.base.get(
      `${config.appUrls.tasks}/api/TaskTemplates/Templates`,
    );

    Promise.all([api.base.get(workflowConfigurationEndpoint), taskTemplatesPromise]).then(
      ([{ data: configurations }, { data: taskTemplates }]) => {
        const translatedConfigurations = configurations
          .map(x => ({
            ...x,
            label: intl.formatMessage(listItemMsg('workflowtype', x.workflowType)),
          }))
          .sort(dynamicSort('label'));
        let translatedTemplates = {};
        taskTemplates.map(t => {
          const mainTaskTranslation = {
            [t.id]: intl.formatMessage(listItemMsg('taskTemplate', t.id, t.name)),
          };
          translatedTemplates = {
            ...translatedTemplates,
            ...mainTaskTranslation,
          };
          if (t.children && t.children.length) {
            t.children.map(x => {
              const subtaskTranslation = {
                [x.id]: intl.formatMessage(listItemMsg('taskTemplate', x.id, x.name)),
              };
              translatedTemplates = {
                ...translatedTemplates,
                ...subtaskTranslation,
              };
            });
          }
        });
        this.setState(state => ({
          ...state,
          configurations: translatedConfigurations,
          translatedTemplates,
        }));
      },
    );
  }

  openEditDialog = workflowConfiguration => {
    const { saving } = this.state;
    if (saving) return;

    this.setState({
      configurationModal: workflowConfiguration,
      showWorkflowConfigurationModal: true,
    });
  };

  handleError = error => {
    const { intl } = this.props;
    handleHttpError(intl)(error);

    this.setState({
      saving: false,
    });
  };

  saveConfiguration = configuration => {
    const { configurations } = this.state;
    if (configuration) {
      const existingConfiguration = configurations.find(o => o.id === configuration.id);
      if (existingConfiguration === undefined) {
        // Add
        this.setState(state => ({
          configurations: [
            {
              ...configuration,
              isNew: true,
            },
            ...state.configurations,
          ],
        }));
      } else {
        // Edit
        this.setState(state => ({
          configurations: state.configurations.map(s => {
            if (s.id === configuration.id) {
              return configuration;
            }
            return s;
          }),
        }));
      }
    }

    this.setState(state => ({
      ...state,
      showWorkflowConfigurationModal: false,
    }));
  };

  deleteConfiguration = configuration => {
    const { saving } = this.state;
    if (saving) return;

    const endpoint = `${config.appUrls.api}/api/workflowConfiguration/Delete/${configuration.id}`;
    this.setState({ saving: true }, () => {
      api.base
        .delete(endpoint)
        .then(() => {
          this.setState(state => ({
            ...state,
            saving: false,
            configurations: state.configurations.filter(s => s.id !== configuration.id),
          }));

          const { intl } = this.props;
          ToastrService.changesSaved(intl);
        })
        .catch(this.handleError);
    });
  };

  render() {
    const {
      configurations,
      configurationModal,
      showWorkflowConfigurationModal,
      translatedTemplates,
    } = this.state;
    const { intl } = this.props;

    return (
      <>
        <div className="panel">
          <div className="panel-heading">
            <h3 className="panel-title">
              <span>
                <FormattedMessage {...messages.WorkflowConfigurationListTitle} />
                <ZendeskHelp article="5434631627794" className="margin5L" />
              </span>
            </h3>
            <CanCreate on="Settings">
              <div className="panel-buttons">
                <button
                  type="button"
                  className="btn btn-inverse"
                  onClick={() => this.openEditDialog(null)}
                >
                  <i className="fa fa-plus" />
                  <FormattedMessage {...messages.Add} />
                </button>
              </div>
            </CanCreate>
          </div>
        </div>
        <WorkflowConfigurationTable
          configurations={configurations}
          taskTemplates={translatedTemplates}
          onDelete={this.deleteConfiguration}
          onEdit={this.openEditDialog}
          intl={intl}
        />

        {showWorkflowConfigurationModal && (
          <WorkflowConfigurationModal
            show
            workflowConfiguration={configurationModal}
            onClose={() =>
              this.setState(state => ({
                ...state,
                showWorkflowConfigurationModal: false,
              }))
            }
            onSave={this.saveConfiguration}
          />
        )}
      </>
    );
  }
}

WorkflowConfigurationContainer.propTypes = {
  intl: intlShape.isRequired,
};

export default showWhenPermission('settings', 'read')(injectIntl(WorkflowConfigurationContainer));
